import {Router, RouteLocationRaw} from "vue-router";
import {InstanceContext} from "@/types/InstanceContext";

/**
 * A localização de rota da página de uma instância.
 *
 * Estava escrita igual em quatro lugares — aqui, no openInstance da classe base das
 * telas, no "abrir em página inteira" do modal e na volta do Kanban de relacionamento.
 */
export function instanceRoute(context: InstanceContext): RouteLocationRaw {
    return {
        name: 'view-instance',
        params: {
            system: context.system,
            idea: context.idea,
            instance: String(context.instance)
        }
    }
}

/**
 * URL real da página de uma instância. Fica fora do componente porque tanto a
 * classe base das telas quanto o PropertyValue precisam montar o mesmo href — os
 * links de instância são <a> com href de verdade, e não router-link, justamente
 * para que ctrl+clique e botão do meio abram a página inteira em nova aba.
 */
export function resolveInstanceHref(router: Router, context: InstanceContext): string {
    return router.resolve(instanceRoute(context)).href
}

/**
 * O clique é para o BROWSER abrir, e não para a tela navegar?
 *
 * Com modificador — ctrl, ⌘, shift, alt — ou com o botão do meio, a resposta é sim: o link tem
 * href de verdade justamente para que a página inteira abra em outra aba ou janela, e chamar
 * `preventDefault` ali tiraria do usuário o que ele pediu.
 *
 * Mora aqui porque são dois a perguntar: as telas de item, pela classe-base, e a paleta de busca,
 * que não é tela e por isso não herda nada dela.
 */
export function opensByItself(
    e: { ctrlKey: boolean, metaKey: boolean, shiftKey: boolean, altKey: boolean, button?: number }
): boolean {
    return e.ctrlKey || e.metaKey || e.shiftKey || e.altKey || (e.button || 0) !== 0
}

/** Qual item é: ambiente, coleção e id. Sem o nonce — é identidade, não estado de tela. */
export function instanceKey(context: InstanceContext): string {
    return `${context.system}/${context.idea}/${context.instance}`
}

/**
 * A chave da entrega da FICHA à página inteira (ver o `expandView` do `ViewInstanceDialog`). À parte
 * da do item, que é da edição: o que se entrega a uma não serve à outra.
 */
export function instanceViewKey(context: InstanceContext): string {
    return `view:${instanceKey(context)}`
}

/**
 * Identidade da ficha na tela, para a `:key` que a remonta.
 *
 * Remontar é o que recarrega: o `load()` roda no `mounted` da folha. O nonce é o que
 * permite recarregar a MESMA instância depois de uma alteração — sem ele a chave não
 * mudaria, e a ficha ficaria com o dado velho.
 */
export function instanceContentKey(context: InstanceContext, nonce: number): string {
    return `${instanceKey(context)}/${nonce}`
}

/**
 * O `changed` de uma ficha que JÁ aplicou a mudança em si mesma: quem hospeda repassa o aviso, mas
 * não a remonta — remontar é reler, e reler aqui seria a leitura mais cara da tela para mostrar o que
 * já está na tela.
 *
 * É o caso do que a ficha faz nas seções de relacionamento: adicionar, vincular, desvincular, mover
 * um card no quadro embutido. Tinha tudo na mão e mesmo assim era relida inteira (relato do usuário:
 * "o padrão de adicionar coisas no kanban é só adicionar mesmo").
 */
export const IN_PLACE = {inPlace: true}

/**
 * A janela da ficha que abrir agora abre JÁ EDITANDO: o lápis do card do Kanban anota antes de pedir
 * a ficha, e a janela lê ao nascer (escolha do usuário: "na janela, já editando").
 *
 * Anotação, e não parâmetro, pelo mesmo motivo do `markOrigin`: entre o card e a janela há quadro,
 * página, ficha e página expandida, cada um com o seu jeito de abrir — um parâmetro passaria por
 * todos. Vale um segundo e é lida uma vez: um clique que não abriu janela não deixa a próxima, aberta
 * por outro caminho, nascer editando.
 */
let editingAt = -Infinity

export function openEditing() {
    editingAt = performance.now()
}

export function takeEditing(): boolean {
    const asked = performance.now() - editingAt < 1000
    editingAt = -Infinity
    return asked
}

export function isInPlace(change: unknown): boolean {
    return !!change && typeof change === 'object' && (change as { inPlace?: boolean }).inPlace === true
}
